/**
 * Mockup tabs (H-2026-09-12-127 Task D) — three-category product preview for
 * the refined result: T-shirt (black + white variants), Mug, Poster. Local
 * canvas composite only (zero SDS on the user path); a tile click hands off
 * to the existingmake-productPOD chain via the onSelect callback.
 *
 * Reuses mockup-composite.js's manifest + composeOne — templates with
 * renderer 'svg' are the v1 shipping form until bao supplies 1024² PNGs.
 */

import { loadManifest, composeOne } from '../../../utils/mockup-composite.js'
import { escAttr } from './shell-scaffold.js'
import { createLogger } from '../../../utils/logger.js'
const log = createLogger('create.dialog.mockup-tabs')

/** Category tabs — order is the tab order; t-shirt first (hero category). */
export const MOCKUP_CATEGORIES = [
  { key: 't-shirt', label: 'T-shirt', match: (t) => (t.product_type || '').toLowerCase() === 't-shirt' },
  { key: 'mug', label: 'Mug', match: (t) => (t.product_type || '').toLowerCase() === 'mug' },
  { key: 'poster', label: 'Poster', match: (t) => (t.product_type || '').toLowerCase() === 'poster' },
]

/**
 * Bucket manifest templates into the three category tabs (uncategorized
 * templates — tote/phonecase — stay off the v2 tabs).
 * @param {Array<object>} templates
 * @returns {Array<{key: string, label: string, templates: Array<object>}>} non-empty tabs
 */
export function groupTemplates(templates) {
  return MOCKUP_CATEGORIES.map((c) => ({
    key: c.key,
    label: c.label,
    templates: templates.filter(c.match),
  })).filter((tab) => tab.templates.length)
}

/**
 * Render the tabbed mockup preview into mountEl.
 * @param {HTMLElement} mountEl
 * @param {string} resultImageUrl - refined result image to composite
 * @param {(template: object, resultImageUrl: string) => void} onSelect - tile click
 */
export async function renderMockupTabs(mountEl, resultImageUrl, onSelect) {
  if (!mountEl) return
  const tabs = groupTemplates(await loadManifest())
  if (!tabs.length) {
    mountEl.innerHTML = ''
    return
  }

  const composed = new Set() // tab keys already composed (lazy per tab)
  mountEl.innerHTML = `
    <div class="mockup-tabs" role="tablist">
      ${tabs.map((t, i) => `
        <button type="button" role="tab" class="mockup-tab${i === 0 ? ' on' : ''}"
                data-tab="${escAttr(t.key)}" aria-selected="${i === 0}">${escAttr(t.label)}</button>`).join('')}
    </div>
    ${tabs.map((t, i) => `
      <div class="mockup-tab-panel${i === 0 ? '' : ' hidden'}" data-panel="${escAttr(t.key)}" role="tabpanel">
        ${t.templates.map((tpl) => placeholderHTML(tpl)).join('')}
      </div>`).join('')}`

  bindClicks(mountEl, tabs, resultImageUrl, composed, onSelect)
  await composeTab(mountEl, tabs[0], resultImageUrl, composed)
}

function placeholderHTML(template) {
  const label = template.color ? `${template.product_type} · ${template.color}` : template.product_type
  return `
    <button
      type="button"
      class="mockup-tile"
      data-template-id="${escAttr(template.id)}"
      data-product-type="${escAttr(template.product_type)}"
      title="Open in Designer with ${escAttr(label)}"
    >
      <img class="mockup-tile-img" alt="${escAttr(label)}" />
      <span class="mockup-tile-label">${escAttr(label)}</span>
    </button>`
}

function bindClicks(mountEl, tabs, resultImageUrl, composed, onSelect) {
  mountEl.querySelectorAll('.mockup-tab').forEach((el) => {
    el.addEventListener('click', () => {
      mountEl.querySelectorAll('.mockup-tab').forEach((t) => {
        t.classList.toggle('on', t === el)
        t.setAttribute('aria-selected', String(t === el))
      })
      mountEl.querySelectorAll('.mockup-tab-panel').forEach((p) => {
        p.classList.toggle('hidden', p.dataset.panel !== el.dataset.tab)
      })
      const tab = tabs.find((t) => t.key === el.dataset.tab)
      if (tab) void composeTab(mountEl, tab, resultImageUrl, composed)
    })
  })
  mountEl.querySelectorAll('.mockup-tile').forEach((el) => {
    el.addEventListener('click', () => {
      const template = tabs.flatMap((t) => t.templates).find((t) => t.id === el.dataset.templateId)
      if (template && onSelect) onSelect(template, resultImageUrl)
    })
  })
}

async function composeTab(mountEl, tab, resultImageUrl, composed) {
  if (composed.has(tab.key)) return
  composed.add(tab.key)
  await Promise.all(tab.templates.map(async (tpl) => {
    try {
      const tile = await composeOne(tpl, resultImageUrl)
      const slot = mountEl.querySelector(`[data-template-id="${tpl.id}"] .mockup-tile-img`)
      if (slot) slot.src = tile.dataUrl
    } catch (err) {
      log.warn('[mockup] compose failed for', tpl.id, err.message)
    }
  }))
}
